import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import type { EmailSettings } from "../lib/types.js";

const SMTP_DEFAULTS = {
  smtpHost: "",
  smtpPort: 587,
  security: "STARTTLS" as const,
};

export function EmailSettingsPage() {
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await api.get<{ emailSettings: EmailSettings }>("/admin/email-settings");
      setSettings(res.emailSettings);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load email settings");
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <h1>Email Sending Settings</h1>
      <p className="muted-subtitle">
        Outbound mail (seat-overage alerts, trial-request acknowledgements) is sent from your own SMTP provider.
        Enter the connection details below.
      </p>
      {error && <p className="error-text">{error}</p>}
      {!settings && !error && <p>Loading…</p>}
      {settings && <EmailSettingsForm settings={settings} onSaved={load} />}
    </div>
  );
}

function EmailSettingsForm({ settings, onSaved }: { settings: EmailSettings; onSaved: () => void }) {
  const [smtpHost, setSmtpHost] = useState(settings.smtpHost || SMTP_DEFAULTS.smtpHost);
  const [smtpPort, setSmtpPort] = useState(settings.smtpPort || SMTP_DEFAULTS.smtpPort);
  const [security, setSecurity] = useState<EmailSettings["security"]>(settings.security || SMTP_DEFAULTS.security);
  const [username, setUsername] = useState(settings.username);
  const [fromEmail, setFromEmail] = useState(settings.fromEmail);
  const [fromName, setFromName] = useState(settings.fromName);
  const [replacePassword, setReplacePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);

  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSaveError(null);
    setSaveOk(false);
    try {
      await api.patch("/admin/email-settings", {
        smtpHost,
        smtpPort,
        security,
        username,
        fromEmail,
        fromName,
        ...(replacePassword ? { password } : {}),
      });
      setSaveOk(true);
      setReplacePassword(false);
      setPassword("");
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save email settings");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleClearPassword() {
    if (!window.confirm("Clear the saved SMTP password? Outbound email will stop sending until a new one is set.")) {
      return;
    }
    setSubmitting(true);
    setSaveError(null);
    try {
      await api.patch("/admin/email-settings", {
        smtpHost,
        smtpPort,
        security,
        username,
        fromEmail,
        fromName,
        clearPassword: true,
      });
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to clear password");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendTest(e: React.FormEvent) {
    e.preventDefault();
    setTesting(true);
    setTestResult(null);
    try {
      await api.post("/admin/email-settings/test", { to: testTo });
      setTestResult({ ok: true, message: `Test email sent to ${testTo}.` });
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : "Failed to send test email" });
    } finally {
      setTesting(false);
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="card">
        <label>
          SMTP host
          <input required value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
        </label>
        <label>
          SMTP port
          <input
            type="number"
            min={1}
            max={65535}
            required
            value={smtpPort}
            onChange={(e) => setSmtpPort(Number(e.target.value))}
          />
        </label>
        <label>
          Security
          <select value={security} onChange={(e) => setSecurity(e.target.value as EmailSettings["security"])}>
            <option value="STARTTLS">STARTTLS (port 587 typically)</option>
            <option value="SSL">SSL/implicit TLS (port 465 typically)</option>
          </select>
        </label>
        <label>
          SMTP username
          <input value={username} onChange={(e) => setUsername(e.target.value)} />
        </label>
        <label>
          From email
          <input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
        </label>
        <label>
          From name
          <input value={fromName} onChange={(e) => setFromName(e.target.value)} />
        </label>

        <div className="card">
          <p>
            SMTP password: <strong>{settings.passwordConfigured ? "configured" : "not configured"}</strong>
            {settings.passwordConfigured && settings.passwordSetAt && (
              <span className="admin-email"> (set {new Date(settings.passwordSetAt).toLocaleString()})</span>
            )}
          </p>
          <label className="checkbox-label">
            <input type="checkbox" checked={replacePassword} onChange={(e) => setReplacePassword(e.target.checked)} />
            Replace password
          </label>
          {replacePassword && (
            <label>
              New SMTP password
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          )}
          {settings.passwordConfigured && (
            <button type="button" className="danger" onClick={handleClearPassword} disabled={submitting}>
              Clear password
            </button>
          )}
        </div>

        <button className="primary" type="submit" disabled={submitting}>
          {submitting ? "Saving…" : "Save settings"}
        </button>
        {saveOk && <p>Saved.</p>}
        {saveError && <p className="error-text">{saveError}</p>}
      </form>

      <form onSubmit={handleSendTest} className="card">
        <h2>Send test email</h2>
        <label>
          Send to
          <input type="email" required value={testTo} onChange={(e) => setTestTo(e.target.value)} />
        </label>
        <button type="submit" disabled={testing || !settings.passwordConfigured}>
          {testing ? "Sending…" : "Send test email"}
        </button>
        {!settings.passwordConfigured && <p className="admin-email">Set a password above and save first.</p>}
        {testResult && (
          <p className={testResult.ok ? undefined : "error-text"}>{testResult.message}</p>
        )}
      </form>
    </>
  );
}
